import React, { useEffect, useState } from "react";
import client from "../lib/api";
import { Apple, Droplet, Beef, Plus, Minus } from "lucide-react";

export default function Diet() {
    const [plans, setPlans] = useState([]);
    const [active, setActive] = useState(0);
    const [water, setWater] = useState(0);

    useEffect(() => {
        client.get("/diet-plans").then((r) => setPlans(r.data));
    }, []);

    if (plans.length === 0) return <div className="p-6 text-zinc-400">Loading meal plans...</div>;
    const plan = plans[active];

    return (
        <div className="px-5 pt-8 pb-8">
            <span className="label-cap text-xs text-neon-green">Diet</span>
            <h1 className="font-display font-black text-4xl uppercase tracking-tight mt-1">Fuel The Beast</h1>

            <div className="mt-6 flex gap-2 overflow-x-auto -mx-5 px-5">
                {plans.map((p, i) => (
                    <button
                        key={p.id}
                        data-testid={`diet-tab-${i}`}
                        onClick={() => setActive(i)}
                        className={`shrink-0 px-4 py-2 label-cap text-xs transition-all ${
                            active === i
                                ? "bg-neon-green text-black"
                                : "surface-1 text-zinc-400 hover:text-white"
                        }`}
                    >
                        {p.name}
                    </button>
                ))}
            </div>

            <div className="mt-6 surface-2 p-4 grid grid-cols-2 gap-3">
                <div>
                    <div className="label-cap text-xs text-zinc-500 flex items-center gap-1">
                        <Apple size={12} /> Calories
                    </div>
                    <div className="font-display font-black text-2xl text-neon-orange">{plan.calories}</div>
                </div>
                <div>
                    <div className="label-cap text-xs text-zinc-500 flex items-center gap-1">
                        <Beef size={12} /> Protein
                    </div>
                    <div className="font-display font-black text-2xl text-neon-green">{plan.protein_target}</div>
                </div>
            </div>

            {/* Water tracker */}
            <div className="mt-4 surface-2 p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Droplet size={16} className="text-neon-green" />
                        <span className="label-cap text-xs">Water Intake</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setWater(Math.max(0, water - 1))}
                            data-testid="water-minus"
                            className="w-7 h-7 surface-3 flex items-center justify-center"
                        >
                            <Minus size={14} />
                        </button>
                        <span className="font-display font-black text-2xl tabular-nums w-12 text-center">{water}</span>
                        <button
                            onClick={() => setWater(water + 1)}
                            data-testid="water-plus"
                            className="w-7 h-7 surface-3 flex items-center justify-center"
                        >
                            <Plus size={14} />
                        </button>
                    </div>
                </div>
                <div className="mt-3 grid grid-cols-8 gap-1">
                    {[...Array(8)].map((_, i) => (
                        <div
                            key={i}
                            className="h-6 transition-colors"
                            style={{
                                background: i < water ? "#39FF14" : "#1f1f1f",
                                boxShadow: i < water ? "0 0 8px #39FF14" : "none",
                            }}
                        />
                    ))}
                </div>
                <div className="text-xs text-zinc-500 mt-2">Target: 8 glasses (~2L)</div>
            </div>

            <h2 className="mt-8 font-display font-black text-2xl uppercase tracking-tight">Daily Meals</h2>
            <div className="mt-3 space-y-3">
                {plan.meals.map((m, i) => (
                    <div key={i} className="surface-1 p-4">
                        <div className="label-cap text-xs text-neon-orange">{m.name}</div>
                        <ul className="mt-2 space-y-1">
                            {m.items.map((it, j) => (
                                <li key={j} className="text-sm text-zinc-300 flex items-start gap-2">
                                    <span className="text-neon-green mt-0.5">▸</span>
                                    <span>{it}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

            {plan.tips && (
                <div className="mt-6 surface-1 p-4 border-l-2 border-l-neon-green">
                    <div className="label-cap text-xs text-neon-green">Tips</div>
                    <ul className="mt-2 space-y-1 text-sm text-zinc-300">
                        {plan.tips.map((t, i) => (
                            <li key={i}>• {t}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="mt-6 surface-1 p-4 border-l-2 border-l-neon-orange">
                <div className="label-cap text-xs text-neon-orange">Train Smart</div>
                <p className="text-xs text-zinc-400 mt-2">
                    Avoid extreme dieting, starvation, or dangerous supplements. Balance is the real flex. Consult a
                    nutritionist if you have specific medical needs.
                </p>
            </div>
        </div>
    );
}
